import React, { useState } from "react";
import { Icons } from "assets/svgs";

/**
 * Icon-badge + title + dashed-divider section wrapper, optionally collapsible.
 * `icon` is a ReactNode (pass e.g. `<Icons.FaFileIcon />`) rather than an icon-name
 * string, so callers don't need a name-lookup table to use it. `bodyClassName`
 * exists because callers disagree on the children wrapper's gap spacing.
 *
 * Collapse state is uncontrolled (`defaultOpen`, the card owns it) unless the
 * caller passes `open` -- then it's controlled and `onToggle` fires instead
 * of the card flipping its own state, for a caller (e.g. an accordion row)
 * that needs to know whether it's open to render something different while
 * collapsed.
 */
export const Card = ({
  icon,
  title,
  tip,
  right,
  className = "",
  bodyClassName = "gap-0.5",
  collapsible = false,
  defaultOpen = false,
  open: openProp,
  onToggle,
  children,
}) => {
  const isControlled = openProp !== undefined;
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isOpen = collapsible ? (isControlled ? openProp : internalOpen) : true;
  const toggle = () => {
    if (!collapsible) return;
    if (isControlled) {
      onToggle?.(!isOpen);
    } else {
      setInternalOpen((o) => !o);
    }
  };
  return (
    <div className={`flex flex-col border border-gray-200 bg-white p-4 shadow-sm ${className}`}>
      {(title || right) && (
        <div
          className={`flex items-center justify-between gap-2 outline-none focus:outline-none focus-visible:outline-none ${
            isOpen ? (title ? "mb-2" : "mb-3") : ""
          } ${collapsible ? "cursor-pointer select-none" : ""}`}
          role={collapsible ? "button" : undefined}
          tabIndex={collapsible ? 0 : undefined}
          onClick={toggle}
          onKeyDown={(e) => {
            if (collapsible && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              toggle();
            }
          }}
        >
          <div className="flex items-center gap-2">
            {title && (
              <>
                {collapsible && (
                  <Icons.FaChevronIcon
                    className={`h-3.5 w-3.5 shrink-0 text-[#437f55] transition-transform ${isOpen ? "" : "-rotate-90"}`}
                  />
                )}
                {/* Same stopPropagation treatment `right` already gets below --
                    icon/title can themselves be interactive (a color-picker
                    button, a rename input), and without this, using them
                    also toggles the accordion via this row's own onClick,
                    which for icon can immediately re-collapse the card and
                    unmount whatever that click just opened. */}
                <span className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  {icon && (
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[#eef6f0] text-[#437f55]">
                      {icon}
                    </span>
                  )}
                  <h3 className="text-sm font-bold text-gray-800">{title}</h3>
                  {tip}
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {right && <span onClick={(e) => e.stopPropagation()}>{right}</span>}
          </div>
        </div>
      )}
      {title && isOpen && <div className="mb-2 border-t border-dashed border-gray-200" />}
      {isOpen && <div className={`flex flex-1 flex-col ${bodyClassName}`}>{children}</div>}
    </div>
  );
};
